<script lang="ts">
  import Button from '$lib/components/ui/button/button.svelte';

  import { slide } from 'svelte/transition';

  import type { Snippet } from 'svelte';
  import { type PhilatelicSet, type StampFromSet, type ReviewState, canReview, canEdit, type WithoutIdAndReview } from '@briefmarken_katalog/types';

  import DateOfIssue from './DateOfIssue.svelte';
  import Title from './Title.svelte';
  import FieldSeparator from './FieldSeparator.svelte';
  import StampInSetRow from './StampInSetRow.svelte';
  import SetImageGrid from './SetImageGrid.svelte';
  import SetEditor from '../editors/SetEditor.svelte';
  import ReviewStateIcon from './ReviewStateIcon.svelte';
  import RowActionButtons from './RowActionButtons.svelte';
  import CatalogDetailDialog from './CatalogDetailDialog.svelte';

  import { user, doLogin } from '$lib/stores/auth';
  import { catalogMode } from '$lib/stores/ui';
  import { reviewSuggestion, getItem, getSuggestion, deleteSuggestion } from '$lib/services/philatelic_items';
  import { SetEditorModel } from '../editors/models/SetEditorModel.svelte';
  import { t } from '$lib/i18n';
  import { applyReviewResult } from './applyReviewResult';

  import './row.css'

  const { set, readonly = false, setLeading, stampLeading }: {
    set: PhilatelicSet;
    readonly?: boolean;
    setLeading?: Snippet;
    stampLeading?: Snippet<[StampFromSet]>;
  } = $props<{
    set: PhilatelicSet;
    readonly?: boolean;
    setLeading?: Snippet;
    stampLeading?: Snippet<[StampFromSet]>;
  }>();

  let isExpanded = $state(false);
  let isEditorOpen = $state(false);
  let editorModel = $state<SetEditorModel | null>(null);
  let catalogDetailItem = $state<PhilatelicSet | null>(null);
  let isCatalogDetailOpen = $state(false);

  async function markAsInReview() {
    try {
      applyReviewResult(set, await reviewSuggestion(set._id!, 'InReview', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  async function markAsSuggested() {
    try {
      applyReviewResult(set, await reviewSuggestion(set._id!, 'Suggested', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  async function fetchFull(): Promise<PhilatelicSet | null> {
    const fetch = set.review.state === 'Accepted' ? getItem : getSuggestion;
    return await fetch(set._id!, doLogin) as PhilatelicSet | null;
  }

  function suggestedBySuffix(full: PhilatelicSet): string {
    return full.review.suggestedByName
      ? ` ${$t('editor.suggestedBy')} ${full.review.suggestedByName}`
      : '';
  }

  async function openReview() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new SetEditorModel({
      isReview: true,
      initialValue: full,
      dialogTitle: 'editor.setReviewTitle',
      dialogSubtitle: 'editor.setReviewSubtitle',
      onCreate: async (updatedSet: WithoutIdAndReview<PhilatelicSet>) => {
        try {
          applyReviewResult(set, await reviewSuggestion(set._id!, set.review.state, updatedSet, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      },
      onReview: async (updatedSet: WithoutIdAndReview<PhilatelicSet>, state: ReviewState) => {
        try {
          applyReviewResult(set, await reviewSuggestion(set._id!, state, updatedSet, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      }
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }

  async function openDelete() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new SetEditorModel({
      isDelete: true,
      initialValue: full,
      dialogTitle: 'deleteSuggestion.title',
      dialogSubtitle: 'deleteSuggestion.subtitle',
      onDelete: async () => {
        try {
          await deleteSuggestion(set._id!, doLogin);
        } catch (e: any) {
          alert($t('errors.deleteSuggestion') + e.message);
        }
      }
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }

  async function openDetails(ro: boolean) {
    const full = await fetchFull();
    if (!full) return;
    // Catalog mode gets the presentation-style detail dialog instead of the editor
    if (ro && $catalogMode) {
      catalogDetailItem = full;
      isCatalogDetailOpen = true;
      return;
    }
    const subtitleKey = set.review.state !== 'Accepted'
      ? 'editor.setDetailsSuggestionSubtitle' as const
      : 'editor.setDetailsCatalogSubtitle' as const;
    editorModel = new SetEditorModel({
      isReadonly: ro,
      isEdit: !ro,
      initialValue: full,
      dialogTitle: 'editor.setDetailsTitle',
      dialogSubtitle: subtitleKey,
      ...(!ro && {
        onCreate: async (updatedSet: WithoutIdAndReview<PhilatelicSet>) => {
          try {
            applyReviewResult(set, await reviewSuggestion(set._id!, set.review.state, updatedSet, doLogin));
          } catch (e: any) {
            alert($t('errors.reviewFailed') + e.message);
          }
        },
      }),
    });
    if (set.review.state !== 'Accepted') editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }
</script>

<!-- Desktop: single flex line (wrappers dissolve via sm:contents / sm:order-*); mobile: stacked lines -->
<div class="row max-sm:flex-col max-sm:gap-1">
  {@render setLeading?.()}
  <!-- mobile line 1: image grid + title -->
  <div class="max-sm:flex max-sm:items-start max-sm:gap-1 max-sm:w-full sm:contents">
    <div class="shrink-0 sm:order-3">
      <SetImageGrid stamps={set.stamps} fetchFullSet={fetchFull}/>
    </div>
    <div class="min-w-0 flex-1 sm:order-5">
      <Title text={set.title}/>
    </div>
  </div>
  <!-- mobile line 2: date · review state · actions -->
  <div class="max-sm:flex max-sm:items-center max-sm:w-full sm:contents">
    <div class="sm:order-1">
      <DateOfIssue date={set.date_of_issue}/>
    </div>
    {#if !readonly}
      <div class="sm:order-6">
        <ReviewStateIcon
          state={set.review.state}
          reviewedBy={set.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          onMarkAsInReview={markAsInReview}
          onDropReview={markAsSuggested}
        />
      </div>
      <div class="max-sm:ml-auto sm:order-8">
        <RowActionButtons
          state={set.review.state}
          reviewedBy={set.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          userCanEdit={!!$user && canEdit(set, $user.id)}
          userIsAdmin={$user?.role === 'Admin'}
          userIsLoggedIn={!!$user}
          onReview={openReview}
          onDelete={openDelete}
          onDetails={openDetails}
        />
      </div>
    {:else}
      <!-- Catalog mode shows the stamps of the set inside the detail dialog, so it needs no expander -->
      <div class="max-sm:ml-auto sm:order-8 flex gap-1">
        {#if $catalogMode}
          <Button class="card-3d card-3d-anim" onclick={() => openDetails(true)}>{$t('buttons.details')}</Button>
        {:else}
          <Button class="card-3d card-3d-anim" onclick={()=>{ isExpanded = !isExpanded; }}>{isExpanded ? $t('buttons.collapse') : $t('buttons.expand')}</Button>
        {/if}
      </div>
    {/if}
  </div>
  <!-- desktop-only vertical dividers -->
  <FieldSeparator class="sm:order-2"/>
  <FieldSeparator class="sm:order-4"/>
  {#if !readonly}
    <FieldSeparator class="sm:order-7"/>
  {/if}
</div>
{#if isExpanded}
<div class="expanded-row" in:slide={{ duration: 300 }} out:slide={{ duration: 300 }}>
  {#if set.description}
    <p class="px-3 py-2 text-sm italic text-muted-foreground">{set.description}</p>
  {/if}
  {#each set.stamps as stamp, i}
    <StampInSetRow
      {stamp}
      {readonly}
      dateOfIssue={set.date_of_issue}
      parentZhihao={set.zhihao}
      positionInSet={i + 1}
      fetchFullImage={() => fetchFull().then(f => f?.stamps?.[i]?.pictures?.[0])}
      fetchFullStamp={() => fetchFull().then(f => f?.stamps?.[i])}
    >
      {#snippet leading()}{@render stampLeading?.(stamp)}{/snippet}
    </StampInSetRow>
  {/each}
</div>
{/if}

{#if isEditorOpen && editorModel}
<SetEditor bind:model={editorModel!} bind:isOpen={isEditorOpen}/>
{/if}

{#if isCatalogDetailOpen && catalogDetailItem}
<CatalogDetailDialog item={catalogDetailItem} bind:isOpen={isCatalogDetailOpen} />
{/if}
